import { Form } from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	ToDoUpdateTileModel,
	ToDoAppDetailsTileModel,
	updateToDoTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";

type ToDoEditFormData = EditTileFromData<ToDoUpdateTileModel>;

const validationBuilder: IValidationConfiguration<ToDoEditFormData> =
	createValidationConfiguration<ToDoEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

export type EditToDoTileProps = {
	initialModel: ToDoAppDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditToDoTile = forwardRef(
	(props: EditToDoTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<ToDoEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow
		});

		const formHandler: IFormHandler<ToDoEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { updateHandler } = useUpdateTile<ToDoUpdateTileModel>(
			formHandler,
			updateToDoTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		useImperativeHandle(
			ref,
			() => ({
				updateHandler
			})
		);

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>

				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</Form>
		);
	}
);

EditToDoTile.displayName = "EditToDoTile";
